import { useState } from "react";
import { Link } from "react-router-dom";
import { forgotPassword, formatApiError } from "@/lib/api";
import { ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const result = await forgotPassword(email);
      if (result?.google_account) {
        setIsGoogleAccount(true);
      } else {
        setSubmitted(true);
      }
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  // Google OAuth account — show sign-in-with-google prompt
  if (isGoogleAccount) {
    return (
      <div className="max-w-md mx-auto px-4 md:px-8 py-16">
        <div className="flex items-center justify-center w-12 h-12 bg-blue-50 border border-blue-200 mb-6 rounded-xl">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          / Google account detected
        </span>
        <h1 className="font-bold text-4xl mt-3 mb-3 text-slate-900">
          Use Google Sign-In.
        </h1>
        <p className="text-sm text-slate-500 mb-6 leading-relaxed">
          <strong className="text-slate-900">{email}</strong> is linked to Google. You don't have a
          separate password — just click <strong className="text-slate-900">Continue with Google</strong>{" "}
          on the sign-in page to access your account.
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white text-xs uppercase tracking-wider font-bold transition-colors rounded-lg"
        >
          Back to sign in <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-md mx-auto px-4 md:px-8 py-16">
        <div className="flex items-center justify-center w-12 h-12 bg-blue-50 border border-blue-200 mb-6 rounded-xl">
          <EnvelopeSimple size={24} className="text-blue-600" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          / Check your inbox
        </span>
        <h1 className="font-bold text-4xl mt-3 mb-3 text-slate-900">
          Email sent.
        </h1>
        <p className="text-sm text-slate-500 mb-6 leading-relaxed">
          If <strong className="text-slate-900">{email}</strong> has an account, a password reset
          link has been sent. Check your inbox and spam folder.
        </p>
        <p className="text-xs text-slate-400 mb-8">
          The link expires in <span className="text-slate-600">30 minutes</span>.
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-blue-600 hover:underline"
        >
          Back to sign in <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 md:px-8 py-16">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        / Forgot password
      </span>
      <h1 className="font-bold text-4xl mt-3 mb-2 text-slate-900">
        Reset your password.
      </h1>
      <p className="text-sm text-slate-500 mb-8">
        Enter your account email and we'll send a reset link.{" "}
        <Link to="/login" className="text-blue-600 hover:underline">
          Back to sign in
        </Link>
      </p>

      <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Email address
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 px-3 py-3 text-sm rounded-lg text-slate-900"
            autoComplete="email"
            data-testid="forgot-email-input"
          />
        </div>

        {err && (
          <div className="border border-red-200 bg-red-50 p-3 text-xs text-red-600 rounded-lg">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-3 text-sm rounded-lg transition-colors inline-flex items-center justify-center gap-2"
          data-testid="forgot-submit-button"
        >
          {busy ? "Sending…" : "Send reset link"} <ArrowRight size={14} weight="bold" />
        </button>
      </form>
    </div>
  );
}
